import { Button, DatePicker, Grid, Popconfirm } from "antd";
import { CalendarPicker } from "antd-mobile";
import dayjs, { Dayjs } from "dayjs";
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import CalendarBlank from "../../assets/images/icons/CalendarBlank";
import { useLoading } from "../../context/LoadingContext";
import { CoachScheduleResponse } from "../../hooks/Coach/data";
import useGetTimezone from "../../hooks/Timezone/timezone.hooks";
import { pushGtag } from "../../plugins/utils";
import { CalendarSelectorProps } from "./data";
import { isAvailable } from "./enumerations/isAvailable";
import "./styles.scss";

const CalendarSelector: React.FC<CalendarSelectorProps> = ({
  setPanelDate,
  schedule,
  form,
  defaultOpen,
  isLoading = false,
  needDisableDate = true,
}) => {
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const { t } = useTranslation();
  const { setIsLoading } = useLoading();
  const { RangePicker } = DatePicker;
  const {
    toServerTimezone,
    toLocalTimezone,
    getTimezoneOffset,
    isAlreadyPassTimeLimit,
  } = useGetTimezone();

  const [open, setOpen] = useState<boolean>(false);
  const [openPopConfirm, setOpenPopConfirm] = useState<boolean>(false);
  const [popupOpen, setPopupOpen] = useState<boolean>(false);
  const [disableDate, setDisableDate] = useState<string[]>([]);
  const [focus, setFocus] = useState<string>("start");
  const [currentPanelDate, setCurrentPanelDate] = useState<Dayjs>();
  const [selectedRange, setSelectedRange] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(null);
  const [pickerValue, setPickerValue] = useState<[Dayjs, Dayjs]>(() => [
    dayjs().month(11).startOf("month"),
    dayjs().month(11).startOf("month"),
  ]);
  const [isScrolling, setIsScrolling] = useState<boolean>(false);
  const defaultStartMonth = useMemo(
    () => dayjs().month(11).startOf("month"),
    []
  );
  const defaultPickerMonths = useMemo<[Dayjs, Dayjs]>(
    () => [defaultStartMonth, defaultStartMonth],
    [defaultStartMonth]
  );
  const pendingStartRef = useRef<Dayjs | null>(null);
  const hasUserNavigatedPanelRef = useRef(false);

  const toPanelMonths = (date: Dayjs): [Dayjs, Dayjs] => {
    const startMonth = date.startOf("month");
    return [startMonth, startMonth.add(1, "month")];
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (screens.md) {
      findPanelDate(
        currentPanelDate
          ? currentPanelDate
          : focus === "start"
            ? selectedRange?.[0] || defaultStartMonth
            : selectedRange?.[1] || defaultStartMonth
      );
      setOpen(true);
      return;
    }
    e.stopPropagation();
    setTimeout(() => {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }, 10);
    setPopupOpen(true);
  };

  const confirmRange = (
    dates: [Dayjs | null, Dayjs | null] | null | undefined
  ) => {
    let isRangeOverDisable = false;
    disableDate.forEach((date: string) => {
      if (
        dayjs(date).isAfter(dates && dates[0]) &&
        dayjs(date).isBefore(dates && dates[1])
      ) {
        isRangeOverDisable = true;
      }
    });

    if (isRangeOverDisable) {
      pendingStartRef.current = null;
      setSelectedRange(null);
      setPickerValue(defaultPickerMonths);
      onSelectDate(undefined);
      setOpen(false);
      setCurrentPanelDate(undefined);
      setOpenPopConfirm(true);
      return;
    }

    if (dates?.[0] && dates[1]) {
      pendingStartRef.current = null;
      setSelectedRange([dates[0], dates[1]]);
      setPickerValue(defaultPickerMonths);
      onSelectDate([dates[0], dates[1]]);
      setOpen(false);
      setCurrentPanelDate(undefined);
      return;
    }

    pendingStartRef.current = null;
    setSelectedRange(null);
    setPickerValue(defaultPickerMonths);
    onSelectDate(undefined);
    setOpen(false);
    setCurrentPanelDate(undefined);
  };

  const handleCalendarChange = (
    dates: [Dayjs | null, Dayjs | null] | null
  ) => {
    // Partial: keep start selected and stay on panel for end date
    if (dates?.[0] && !dates[1]) {
      pendingStartRef.current = dates[0];
      setSelectedRange([dates[0], null]);
      setPickerValue(toPanelMonths(dates[0]));
      findPanelDate(dates[0]);
      setCurrentPanelDate(dates[0]);
      return;
    }

    // Complete range: confirm immediately.
    // Do not wait for onChange — with controlled value/open, rc-picker may
    // defer onChange until the end field is focused/clicked again.
    if (dates?.[0] && dates[1]) {
      confirmRange(dates);
    }
  };

  const handleRangeChange = (
    dates: [Dayjs | null, Dayjs | null] | null,
    _dateStrings: [string, string]
  ) => {
    if (dates?.[0] && !dates[1]) {
      return;
    }
    confirmRange(dates);
  };

  const findPanelDate = (date: Dayjs) => {
    // FIXME: Should catch first day and last day in panel
    const twoMonthBefore = date
      .add(-2, "month")
      .startOf("month")
      .format("YYYY-MM-DD");
    const twoMonthAfter = date
      .add(+2, "month")
      .endOf("month")
      .format("YYYY-MM-DD");
    setPanelDate({
      start_date: toServerTimezone(twoMonthBefore)?.format(
        "YYYY-MM-DDTHH:mm:ss[Z]"
      ),
      end_date: toServerTimezone(twoMonthAfter)?.format(
        "YYYY-MM-DDTHH:mm:ss[Z]"
      ),
    });
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!screens.sm) {
      // Block RangePicker dropdown from opening on mobile
      setOpen(false);
      return;
    }
    // Must follow the value from RangePicker; toggling can reopen after
    // selecting the end date and block auto-confirm close.
    setOpen(nextOpen);
  };

  const handlePanelChange = (value: [Dayjs | null, Dayjs | null]) => {
    hasUserNavigatedPanelRef.current = true;
    const anchorMonth = selectedRange?.[0]?.startOf("month");
    const nextPickerValue: [Dayjs, Dayjs] = [
      value[0] ?? anchorMonth ?? pickerValue[0],
      value[1] ?? anchorMonth?.add(1, "month") ?? pickerValue[1],
    ];
    setPickerValue(nextPickerValue);
    const newCurrentPanelDate = dayjs(
      focus === "start" ? nextPickerValue[0] : nextPickerValue[1]
    );
    findPanelDate(newCurrentPanelDate);
    setCurrentPanelDate(newCurrentPanelDate);
  };

  const handlePickerValueChange = (value: [Dayjs | null, Dayjs | null]) => {
    const anchor = pendingStartRef.current ?? selectedRange?.[0];
    const isSelectingEnd =
      Boolean(anchor) && (selectedRange?.[1] === null || !selectedRange?.[1]);

    if (isSelectingEnd && anchor) {
      setPickerValue([
        value[0] ?? anchor.startOf("month"),
        value[1] ?? pickerValue[1],
      ]);
      return;
    }

    if (!selectedRange && !hasUserNavigatedPanelRef.current) {
      return;
    }

    setPickerValue([
      value[0] ?? pickerValue[0],
      value[1] ?? pickerValue[1],
    ]);
  };

  const handleFocus = (event: any, { range }: any) => {
    // console.debug("range", range);
    setFocus(range);
  };

  const renderExtraFooter = () => {
    return (
      <Button
        type="link"
        onClick={() => {
          handleRangeChange(null, ["", ""]);
          pushGtag({
            event: "cancel_calendar",
          });
        }}
      >
        {t("normal.cancel")}
      </Button>
    );
  };

  const onSelectDate = (val: [Dayjs, Dayjs] | undefined) => {
    // console.debug("onSelectDate", val);
    if (val) {
      const startDateString = val[0].format(
        `YYYY-MM-DD 00:00:00${getTimezoneOffset(
          process.env.REACT_APP_TIMEZONE
        )}`
      );
      const endDateString = val[1].format(
        `YYYY-MM-DD 23:59:59${getTimezoneOffset(
          process.env.REACT_APP_TIMEZONE
        )}`
      );
      form.setFieldsValue({
        start_date: toServerTimezone(startDateString)?.format(
          "YYYY-MM-DDTHH:mm:ss[Z]"
        ),
        end_date: toServerTimezone(endDateString)?.format(
          "YYYY-MM-DDTHH:mm:ss[Z]"
        ),
        date: true,
      });
    } else {
      form.setFieldsValue({
        date: undefined,
      });
    }
  };

  useEffect(() => {
    if (!schedule) return;
    // console.debug("schedule", schedule);
    const newDisableDate: any[] = [];
    schedule.map((item: CoachScheduleResponse) => {
      if (item.is_available === isAvailable.DISABLE)
        newDisableDate.push(toLocalTimezone(item.date)?.format("YYYY-MM-DD"));
      return item;
    });
    const today = toLocalTimezone(dayjs())?.format("YYYY-MM-DD");
    const set = new Set(newDisableDate);
    set.add(today);
    if (isAlreadyPassTimeLimit()) {
      const tomorrow = toLocalTimezone(dayjs().add(1, "day"))?.format(
        "YYYY-MM-DD"
      );
      set.add(tomorrow);
    }
    setDisableDate(Array.from(set));
  }, [schedule]);

  useEffect(() => {
    // findPanelDate(dayjs());
    findPanelDate(defaultStartMonth);
  }, []);

  useEffect(() => {
    if (defaultOpen !== undefined) {
      setOpen(defaultOpen);
      return;
    }
    if (screens.md === true) setOpen(true);
  }, [screens, defaultOpen]);

  useLayoutEffect(() => {
    if (!screens.md || !open || selectedRange) return;
    hasUserNavigatedPanelRef.current = false;
    setPickerValue(defaultPickerMonths);
  }, [open, screens.md, selectedRange, defaultPickerMonths]);

  const [visibleMonth, setVisibleMonth] = useState<string>("");
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const loadingStartTime = useRef<number | null>(null);
  const scrollLoadingTimer = useRef<NodeJS.Timeout | null>(null);
  const isLoadingRef = useRef<boolean>(isLoading);
  const calendarPickerRef = useRef<any>(null);
  const skipNextScrollLoadingRef = useRef<boolean>(false);
  const SCROLL_TRESHOLD = 150;

  const onScroll = (scrollContainer: Element) => {
    setIsScrolling(true);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (scrollLoadingTimer.current) clearTimeout(scrollLoadingTimer.current);

    debounceTimer.current = setTimeout(() => {
      const monthElements =
        scrollContainer.querySelectorAll("[data-year-month]");
      monthElements.forEach((el) => {
        const rect = (el as HTMLElement).getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();
        const difference = rect.top - containerRect.top;
        if (difference < SCROLL_TRESHOLD) {
          const title = el?.getAttribute("data-year-month");
          if (title) {
            setVisibleMonth(title);
          }
          return false; // break out of forEach
        }
        setTimeout(() => {
          setIsScrolling(false);
        }, 1000);
        return true;
      });

      // 跳過本次首次滾動（通常是初次定位月份造成的自動滾動）
      if (skipNextScrollLoadingRef.current) {
        skipNextScrollLoadingRef.current = false;
        return;
      }

      // 滾動完成後，設置 loading 為 true
      setIsLoading(true);
      loadingStartTime.current = Date.now();

      // 1.5 秒後，再判斷 isLoading 的狀態
      scrollLoadingTimer.current = setTimeout(() => {
        if (isLoadingRef.current) return;
        setIsLoading(isLoadingRef.current);
        loadingStartTime.current = null;
      }, 1500);
    }, 200);
  };

  const handleCalendarConfirm = (values: [Date, Date] | null) => {
    // console.debug("dates", values);
    let isRangeOverDisable = false;
    disableDate.map((date: string) => {
      if (
        dayjs(date).isAfter(values && values[0]) &&
        dayjs(date).isBefore(values && values[1])
      )
        isRangeOverDisable = true;
      return date;
    });

    if (isRangeOverDisable) {
      setSelectedRange(null);
      onSelectDate(undefined);
      setOpenPopConfirm(true);
    } else {
      if (values && values[0] && values[1]) {
        const [start, end] = values.map((d) => dayjs(d));
        console.debug(
          "Selected from mobile calendar:",
          start.format("YYYY-MM-DD"),
          end.format("YYYY-MM-DD")
        );
        setSelectedRange([start, end]);
        onSelectDate([start, end]);
      } else {
        setSelectedRange(null);
        onSelectDate(undefined);
      }
    }
  };

  const shouldDisableDate = (date: Date) => {
    if (isScrolling && needDisableDate) return true;
    const current: Dayjs = dayjs(date);
    return (
      (current && current < dayjs().endOf("day")) ||
      disableDate.includes(current.format("YYYY-MM-DD"))
    );
  };

  useEffect(() => {
    if (!popupOpen) return;
    skipNextScrollLoadingRef.current = true;
    let scrollContainer: Element | null = null;
    let attachTimer: NodeJS.Timeout | null = null;

    const handleScroll = () => {
      if (!scrollContainer) return;
      onScroll(scrollContainer);
    };

    const tryAttachScroll = () => {
      const popup = document.querySelector(".adm-calendar-picker-popup");
      if (!popup) {
        attachTimer = setTimeout(tryAttachScroll, 50);
        return;
      }
      scrollContainer = popup.querySelector(".adm-calendar-picker-view-body");
      if (!scrollContainer) {
        attachTimer = setTimeout(tryAttachScroll, 50);
        return;
      }
      scrollContainer.addEventListener("scroll", handleScroll);
    };

    tryAttachScroll();

    return () => {
      if (attachTimer) clearTimeout(attachTimer);
      if (scrollContainer)
        scrollContainer.removeEventListener("scroll", handleScroll);
      if (scrollLoadingTimer.current) clearTimeout(scrollLoadingTimer.current);
    };
  }, [popupOpen]);

  useEffect(() => {
    if (screens.md === true) return;
    if (!visibleMonth) return;
    findPanelDate(dayjs(`${visibleMonth}-01`, "YYYY-M-DD"));
  }, [visibleMonth, screens]);

  useEffect(() => {
    if (!popupOpen) return;
    const jumpTimer = setTimeout(() => {
      calendarPickerRef.current?.jumpTo?.({
        year: defaultStartMonth.year(),
        month: defaultStartMonth.month() + 1,
      });
    }, 0);
    return () => clearTimeout(jumpTimer);
  }, [popupOpen, defaultStartMonth]);

  useEffect(() => {
    isLoadingRef.current = isLoading;

    // 如果外部 isLoading 變為 false，且距離 loading 開始已超過 1.5 秒，立即關閉 loading
    if (!isLoading && loadingStartTime.current) {
      const elapsedTime = Date.now() - loadingStartTime.current;
      if (elapsedTime >= 1500) {
        setIsLoading(false);
        loadingStartTime.current = null;
      }
    }
  }, [isLoading, setIsLoading]);

  return (
    <>
      <Popconfirm
        title={t("calendar.hint.title")}
        description={t("calendar.hint.description")}
        open={openPopConfirm}
        onConfirm={() => setOpenPopConfirm(false)}
        showCancel={false}
        okText={t("calendar.hint.ok")}
        icon={false}
      >
        <RangePicker
          disabledDate={(current) => {
            // return disableDate.includes(current.format("YYYY-MM-DD"));
            return (
              (current && current < dayjs().endOf("day")) ||
              disableDate.includes(current.format("YYYY-MM-DD"))
            );
          }}
          value={selectedRange}
          pickerValue={pickerValue}
          onPickerValueChange={handlePickerValueChange}
          open={screens.md ? open : false}
          onClick={handleClick}
          onFocus={handleFocus}
          onOpenChange={handleOpenChange}
          onCalendarChange={handleCalendarChange}
          onChange={handleRangeChange}
          onPanelChange={handlePanelChange}
          style={{ width: "100%" }}
          renderExtraFooter={renderExtraFooter}
          readOnly
          placeholder={[
            t("home.searchbar.date.start"),
            t("home.searchbar.date.end"),
          ]}
          prefix={
            screens.lg ? null : (
              <CalendarBlank
                style={{
                  fontSize: 24,
                  color: "var(--Neutral-dark-80)",
                }}
              />
            )
          }
          suffixIcon={
            screens.lg ? (
              <CalendarBlank
                style={{
                  fontSize: 24,
                  color: "var(--Neutral-dark-80)",
                }}
              />
            ) : null
          }
        />
        <CalendarPicker
          ref={calendarPickerRef}
          className="calendar-picker"
          title={t("calendar.picker.title")}
          visible={popupOpen}
          selectionMode={"range"}
          onClose={() => setPopupOpen(false)}
          onMaskClick={() => setPopupOpen(false)}
          min={new Date()}
          max={dayjs().add(5, "year").toDate()}
          shouldDisableDate={shouldDisableDate}
          onConfirm={handleCalendarConfirm}
          confirmText={t("normal.confirm")}
          renderTop={() => false}
        />
      </Popconfirm>
    </>
  );
};

export default CalendarSelector;
